// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseInput, BaseSelect, Loading, UnderDevelopment } from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { IQueryParamService, IService } from '~/utils/interface/service';
import { IMaterial, IQueryParamMaterial } from '~/utils/interface/material';
import { IBaseOption } from '~/utils/interface/common';
import { defaultValuesForm, schema } from './helper';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  BACK_TO_PREVIOUS_PAGE,
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { IFormPriceBook } from '~/utils/interface/priceBook';
import { compareDataUpdate, removeEmptyObjects } from '~/utils/helper/common';
import { getServiceList } from '~/thunks/service/serviceThunk';
import { getMaterialList } from '~/thunks/material/materialThunk';
import { createPriceBook, getPriceBookDetail, updatePriceBook } from '~/thunks/priceBook/priceBookThunk';
// Styles, images, icons
import styles from './FormPriceBook.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const FormPriceBook = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/priceBook', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: priceBookId } = useParams();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormPriceBook>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [isLoadingService, setIsLoadingService] = useState<boolean>(false);
  const [isLoadingMaterial, setIsLoadingMaterial] = useState<boolean>(false);
  const [serviceSearchKey, setServiceSearchKey] = useState<string>(EMPTY_STRING);
  const [materialSearchKey, setMaterialSearchKey] = useState<string>(EMPTY_STRING);
  const debounceServiceSearchKey = useDebounce(serviceSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const debounceMaterialSearchKey = useDebounce(materialSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [serviceOptions, setServiceOptions] = useState<IBaseOption[]>([]);
  const [materialOptions, setMaterialOptions] = useState<IBaseOption[]>([]);

  const [initialData, setInitialData] = useState<IFormPriceBook>(defaultValuesForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamService = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceServiceSearchKey && { textSearch: debounceServiceSearchKey }),
    };

    handleGetServiceList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceServiceSearchKey]);

  useEffect(() => {
    const params: IQueryParamMaterial = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceMaterialSearchKey && { textSearch: debounceMaterialSearchKey }),
    };

    handleGetMaterialList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceMaterialSearchKey]);

  useEffect(() => {
    if (!priceBookId) return;

    handleGetPriceBookDetail(priceBookId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBookId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetServiceList = (params: IQueryParamService) => {
    setIsLoadingService(true);

    dispatch(getServiceList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;

        if (!responses) return;

        handleConvertServiceOptions(responses);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoadingService(false);
      });
  };

  const handleGetMaterialList = (params: IQueryParamMaterial) => {
    setIsLoadingMaterial(true);

    dispatch(getMaterialList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;

        if (!responses) return;

        handleConvertMaterialOptions(responses);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoadingMaterial(false);
      });
  };

  const handleGetPriceBookDetail = (priceBookId: string) => {
    if (!priceBookId) return;

    setIsLoading(true);

    dispatch(getPriceBookDetail(priceBookId))
      .unwrap()
      .then((res) => {
        const priceBookDetail = res?.data;

        const serviceIds: string[] = priceBookDetail?.services?.map((service) => service?.id);
        const materialIds: string[] = priceBookDetail?.materials?.map((material) => material?.id);

        const newData: IFormPriceBook = {
          name: priceBookDetail?.name || EMPTY_STRING,
          serviceIds: serviceIds || [],
          materialIds: materialIds || [],
        };

        setInitialData(newData);
        reset(newData);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitForm = (data: IFormPriceBook) => {
    if (priceBookId) {
      handleEditPriceBook(data);
    } else {
      handleAddPriceBook(data);
    }
  };

  const handleAddPriceBook = (data: IFormPriceBook) => {
    setIsLoading(true);

    const body: IFormPriceBook = removeEmptyObjects(data) as IFormPriceBook;

    dispatch(createPriceBook(body))
      .unwrap()
      .then(() => {
        handleCancelForm();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditPriceBook = (data: IFormPriceBook) => {
    const dataChange: IFormPriceBook = compareDataUpdate(initialData, data) as IFormPriceBook;

    if (!priceBookId || !dataChange) return;

    setIsLoading(true);

    dispatch(updatePriceBook({ priceBookId, body: dataChange }))
      .unwrap()
      .then((res) => {
        handleCancelForm();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleSearchServiceChange = (searchKey: string) => {
    setServiceSearchKey(searchKey);
  };

  const handleSearchMaterialChange = (searchKey: string) => {
    setMaterialSearchKey(searchKey);
  };

  const handleConvertServiceOptions = (serviceList: IService[]) => {
    const options: IBaseOption[] = serviceList.map((service) => ({
      value: service?.id,
      label: service?.name || EMPTY_STRING,
    }));

    setServiceOptions(options);
  };

  const handleConvertMaterialOptions = (material: IMaterial[]) => {
    const options: IBaseOption[] = material.map((material) => ({
      value: material?.id,
      label: material?.name || EMPTY_STRING,
    }));

    setMaterialOptions(options);
  };

  const handleCancelForm = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset(defaultValuesForm);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formPriceBookPage'
      title={t(priceBookId ? 'form_price_book_edit_title' : 'form_price_book_add_title')}
      onBackToPreviousPage={handleCancelForm}
    >
      <Stack
        component={'form'}
        spacing={'20px'}
        onSubmit={handleSubmit(handleSubmitForm)}
        className={cx('form')}
      >
        <Box component={'div'} className={cx('formBody')}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='name'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_price_book_label_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.name?.message}
                      required
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='serviceIds'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      multiple={true}
                      label={t('form_price_book_label_services')}
                      value={value || EMPTY_STRING}
                      onChange={onChange}
                      isLoading={isLoadingService}
                      options={serviceOptions}
                      onSearchChange={handleSearchServiceChange}
                      errorMessage={errors.serviceIds?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='materialIds'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      multiple={true}
                      label={t('form_price_book_label_materials')}
                      value={value || EMPTY_STRING}
                      onChange={onChange}
                      isLoading={isLoadingMaterial}
                      options={materialOptions}
                      onSearchChange={handleSearchMaterialChange}
                      errorMessage={errors.materialIds?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Box>

        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='outlined' onClick={handleCancelForm}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={priceBookId ? !isDirty : false}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>

        {isLoading && <Loading />}

        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
      </Stack>
    </ChildrenPage>
  );
};

export default FormPriceBook;
